import React, { ReactElement, useEffect } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { useThemeStyle } from '@/modules/hooks/useThemeStyle';
import {
  useGetElearningBehaviourSettingsQuery,
  useGetElearningQuery,
  useGetQuestionQuery,
  useGetQuizQuery,
} from '@/modules/services';
import PlayerUi from '@/components/Player';
import { getCorrectChoices, useIsMobileScreenSize } from '@/lib/util';
import { Spinner } from 'elmo-elements';
import Bottom from '@/components/Player/Question/components/Bottom';
import Feedback from '@/components/Player/Question/components/Feedback';
import Header from '@/components/Player/Question/components/Header';
import PreviewFeedbackRoutes, { PreviewFeedbackParamType } from './route';
import MyLibraryRoutes from '../MyLibrary/MyLibraryRoutes';
import QuizRoutes from '../Quiz/routes';
import { useModuleStyle } from '@/modules/hooks/useModuleStyle';

const PreviewFeedback = (): ReactElement => {
  const history = useHistory();
  const { elearningId, questionId, type } =
    useParams<PreviewFeedbackParamType>();
  const elearning = useGetElearningQuery(elearningId);
  const quiz = useGetQuizQuery(elearningId);
  const question = useGetQuestionQuery(
    {
      questionId,
    },
    {
      skip: !quiz.data?.id || !quiz.data,
    },
  );
  const {
    data: elearningBehaviourSettingsData,
    isLoading: elearningBehaviourSettingsIsLoading,
  } = useGetElearningBehaviourSettingsQuery();

  useThemeStyle(elearning.data?.theme);
  useModuleStyle(elearning.data);

  const { isMobile } = useIsMobileScreenSize();

  useEffect(() => {
    // Route parameters validation
    if (elearning.isLoading || quiz.isLoading || question.isLoading) return;

    switch (true) {
      case !elearning.isSuccess || !quiz.isSuccess:
        // On invalid elearningId or quizId re-route to my-library
        history.replace(generatePath(MyLibraryRoutes.path));
        break;

      case !question.isSuccess:
        // On invalid questionId re-route to quiz
        history.replace(generatePath(QuizRoutes.path, { elearningId }));
        break;
      case type !== 'incorrect' && type !== 'correct':
        // On invalid type - use correct as default feedback preview
        history.replace(
          generatePath(PreviewFeedbackRoutes.path, {
            elearningId,
            questionId,
            type: 'correct',
          }),
        );
        break;

      default:
        break;
    }
  }, [elearning, quiz, question, elearningId, questionId, type]);

  if (
    question.isLoading ||
    quiz.isLoading ||
    elearningBehaviourSettingsIsLoading ||
    !question.data ||
    !quiz.data
  ) {
    return <Spinner />;
  }

  return (
    <PlayerUi
      isPreview={true}
      isPlayable={true}
      currentFrameId=""
      elearning={elearning.data}
      isQuizEnabled={false}
      frames={[]}
      resources={[]}
      helperContent={[]}
      helperFaq={[]}
      isMobileView={isMobile}
      elearningBehaviourSettings={elearningBehaviourSettingsData}
      canvas={
        <div id="Quiz">
          <div id="page" className="test-content">
            <div id="status-bar">
              <div className="steps">
                <span className="sr-only">Question </span>
                <span id="current-no">1</span> of <span id="total-no">1</span>
              </div>
            </div>
            <div id="replace-content">
              <Header content={question.data.content} />
              <div id="choice-part">
                <Feedback
                  questionType={question.data.questionMetadata.type}
                  isAnswerCorrect={type === 'correct'}
                  selectedAnswer={{
                    data: {
                      content: 'Your selected answer will be shown here.',
                    },
                  }}
                  correctAnswer={getCorrectChoices(question.data).map(
                    (choice) => choice.content,
                  )}
                  questionFeedback={{
                    correct: question.data?.feedbackMetadata.feedback1,
                    inCorrect: question.data?.feedbackMetadata.feedback2,
                  }}
                  showCorrectAnswer={quiz.data.quizMetadata.showCorrect}
                  showFeedback={1}
                />
              </div>
              <Bottom continueHandler={() => {}} />
            </div>
          </div>
        </div>
      }
    />
  );
};

export default PreviewFeedback;
